import React, { useState, useEffect } from 'react';
import { PermissionGroup, Permission, PermissionGroupFormData } from '../types';
import SidePanel from '../../../UI/SidePanel';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import styles from '../../../../styles/PermissionManagement.module.css';
import apiService from '../../../../services/api';

interface AddEditPermissionGroupPanelProps {
  isOpen: boolean;
  editingGroup: PermissionGroup | null;
  formData: PermissionGroupFormData;
  onClose: () => void;
  onSubmit: () => void;
  onInputChange: (field: string, value: any) => void;
}

const AddEditPermissionGroupPanel: React.FC<AddEditPermissionGroupPanelProps> = ({
  isOpen,
  editingGroup,
  formData,
  onClose,
  onSubmit,
  onInputChange
}) => {
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAllPermissions();
    }
  }, [isOpen]);

  const loadAllPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const response = await apiService.getAllPermissions();
      if (response.status === 'success') {
        setAllPermissions((response as any).permissions || []);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    const module = perm.module_display || perm.module.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Use groupedPermissions directly without filtering

  const togglePermission = (permissionId: string) => {
    const currentPermissions = formData.permissions || [];
    if (currentPermissions.includes(permissionId)) {
      onInputChange('permissions', currentPermissions.filter(id => id !== permissionId));
    } else {
      onInputChange('permissions', [...currentPermissions, permissionId]);
    }
  };


  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={editingGroup ? 'Edit Permission Group' : 'Add Permission Group'}
      icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      }
    >
      <form className={sidePanelStyles.form} onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}>
        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>
            Name <span className={sidePanelStyles.required}>*</span>
          </label>
          <input
            type="text"
            className={sidePanelStyles.formControl}
            value={formData.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            placeholder="Enter permission group name"
            required
          />
        </div>

        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>Description</label>
          <textarea
            className={sidePanelStyles.formControl}
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Enter permission group description"
            rows={3}
            style={{ resize: 'vertical', minHeight: '80px' }}
          />
        </div>

        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>Status</label>
          <div className={styles.toggleContainer}>
            <div className={styles.toggleLabel}>
              <div className={styles.toggleTitle}>Enable Permission Group</div>
              <div className={styles.toggleDescription}>Activate this permission group to make it available for assignment</div>
            </div>
            <label className={styles.toggleSwitch}>
              <input 
                type="checkbox" 
                checked={formData.is_active}
                onChange={(e) => onInputChange('is_active', e.target.checked)}
              />
              <span className={styles.toggleSlider}></span>
            </label>
          </div>
        </div>

        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>
            Permissions <span className={sidePanelStyles.required}>*</span>
          </label>
          
          {loadingPermissions ? (
            <div className={styles.loadingPermissions}>
              <div className={styles.loadingSpinner}></div>
              <span>Loading permissions...</span>
            </div>
          ) : (
            <div className={styles.permissionsContent}>
              {Object.entries(groupedPermissions).map(([module, perms]) => (
                <div key={module} className={styles.permissionCategory}>
                  <div className={styles.categoryHeader}>
                    <h6 className={styles.categoryTitle}>{module}</h6>
                    <span className={styles.moduleCount}>
                      {perms.filter(p => formData.permissions?.includes(p.id)).length}/{perms.length}
                    </span>
                  </div>
                  <div className={styles.permissionsGrid}>
                    {perms.map(perm => {
                      const isSelected = formData.permissions?.includes(perm.id) || false;
                      return (
                        <button
                          key={perm.id}
                          type="button"
                          className={`${styles.permissionButton} ${isSelected ? styles.permissionButtonActive : ''}`}
                          onClick={() => togglePermission(perm.id)}
                        >
                          {perm.action_display || perm.action}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {Object.keys(groupedPermissions).length === 0 && (
                <div className={styles.noPermissionsFound}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  <p>No permissions available</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={sidePanelStyles.formActions}>
          <button
            type="button"
            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
          >
            {editingGroup ? 'Update' : 'Create'} Permission Group
          </button>
        </div>
      </form>
    </SidePanel>
  );
};

export default AddEditPermissionGroupPanel;

